import { format, parseISO } from "date-fns";
import React, { useState } from "react";
import { z } from "zod";

import { ICompanyBillingSettingsFrontend } from "@performyard/models";
import { CalculateProratedTotalResponse } from "@performyard/service-contracts";

import Alert from "../../../../components/Alert";
import Button from "../../../../components/Button";
import InputText from "../../../../components/Inputs/InputText";
import ContentModal from "../../../../components/Modal/Templates/ContentModal";
import Typography from "../../../../components/Text";

import { useCalculateProratedTotal, useCreateAddSeatsPaymentIntent } from "../../../../api/hooks";
import useForm from "../../../../hooks/useForm";

interface AddUsersModalProps {
	isOpen: boolean;
	onClose: () => void;
	company: ICompanyBillingSettingsFrontend;
}

// Utilities
const formatDate = (dateString: string): string => {
	const parsedDate = parseISO(dateString);
	// Force time to noon UTC to prevent date shifting
	const utcPreservedDate = new Date(
		Date.UTC(parsedDate.getUTCFullYear(), parsedDate.getUTCMonth(), parsedDate.getUTCDate(), 12, 0, 0)
	);

	return format(utcPreservedDate, "MMMM d, yyyy");
};

const createAddUsersFormSchema = (minUsers: number) =>
	z.object({
		quantity: z
			.string()
			.refine((val) => {
				const num = Number(val);
				return !isNaN(num) && Number.isInteger(num);
			}, "Quantity must be a whole number")
			.refine(
				(val) => {
					const num = Number(val);
					return num >= minUsers;
				},
				`Must add at least ${minUsers} seat${minUsers > 1 ? "s" : ""}`
			),
	});

// Sub-components
const PricingInfo = ({
	calculatedAmounts,
	company,
}: {
	calculatedAmounts: CalculateProratedTotalResponse;
	company: ICompanyBillingSettingsFrontend;
}) => (
	<div className="mt-4">
		<Typography size="paragraph-3" weight="regular" className="mb-2">
			<span className="font-bold">{calculatedAmounts.usersToAdd}</span> seats, pro-rated through{" "}
			<span className="font-bold">{formatDate(company.billing_expires)}</span>
		</Typography>

		<Typography size="paragraph-3" weight="regular" className="mb-2">
			<span className="font-bold text-green-600">${calculatedAmounts.formattedProratedPrice}</span> pro-rated
			price per seat.
		</Typography>

		<Typography size="paragraph-3" weight="regular" className="mb-2">
			<span className="font-bold text-green-600">${calculatedAmounts.formattedBillingPrice}</span> total seat
			purchase.
		</Typography>

		<Typography size="paragraph-3" weight="regular" className="mb-2">
			<span className="font-bold text-green-600">${calculatedAmounts.formattedTotalTax}</span> sales tax amount.
		</Typography>

		<Typography size="paragraph-3" weight="regular" className="mb-2">
			<span className="font-bold text-green-600">${calculatedAmounts.formattedTotalPrice}</span> total to be
			billed to your credit card when you click Add Seats below, in accordance with your original order form.
		</Typography>
	</div>
);

const MinimumSeatsMessage = ({ minUsers }: { minUsers: number }) => (
	<div className="mt-4">
		<Typography size="paragraph-3" weight="semi-bold">
			You must add at least {minUsers} seat(s).
		</Typography>
	</div>
);

const CalculationErrorMessage = ({ calculationError }: { calculationError: string }) => (
	<div className="mt-2">
		<Alert type="error" message={calculationError} />
	</div>
);

const AddUsersModal = ({ isOpen, onClose, company }: AddUsersModalProps) => {
	const formSchema = createAddUsersFormSchema(company.min_users_to_add || 1);

	const { state, updateForm, validate, resetForm } = useForm({
		schema: formSchema,
		defaultValues: { quantity: "" },
	});

	const [validationError, setValidationError] = useState<string | undefined>();
	const [calculationError, setCalculationError] = useState<string | undefined>();

	const {
		mutate: calculateTotal,
		data: calculatedAmounts,
		isPending: isCalculationPending,
		reset: resetCalculations,
	} = useCalculateProratedTotal(setCalculationError);

	const handleCloseModal = () => {
		onClose();
		resetForm();
		resetCalculations();
		setValidationError(undefined);
	};

	const { mutate: submitPayment, isPending: isPaymentPending } = useCreateAddSeatsPaymentIntent(handleCloseModal);

	const handleGetPrice = () => {
		const validation = validate();
		if (validation.isValid) {
			setValidationError(undefined);
			calculateTotal(validation.data.quantity);
		} else {
			const firstError = Object.values(validation.errors)[0];
			setValidationError(firstError?.message);
		}
	};

	const handleAddSeats = () => {
		const validation = validate();
		if (validation.isValid && calculatedAmounts) {
			setValidationError(undefined);
			submitPayment({
				quantity: validation.data.quantity,
				totalAmount: calculatedAmounts.formattedTotalPrice,
			});
		} else if (!validation.isValid) {
			const firstError = Object.values(validation.errors)[0];
			setValidationError(firstError?.message);
		}
	};

	const handleInputChange = (val: string) => {
		updateForm({ quantity: val });
		resetCalculations();
		setValidationError(undefined);
		setCalculationError(undefined);
	};

	const isSubmitDisabled =
		isCalculationPending ||
		isPaymentPending ||
		!state.quantity ||
		!Number.isInteger(Number(state.quantity)) ||
		parseInt(state.quantity) < (company?.min_users_to_add || 1);

	const submitAction = calculatedAmounts ? handleAddSeats : handleGetPrice;
	const submitText = calculatedAmounts ? "Add Seats" : "Get Price";

	return (
		<ContentModal
			size="small"
			title="Add More Seats"
			isOpen={isOpen}
			handleClose={handleCloseModal}
			buttons={[
				<Button key="submit" color="green" onClick={submitAction} disabled={isSubmitDisabled}>
					{submitText}
				</Button>,
			]}
		>
			<InputText
				type="number"
				value={state.quantity}
				onChange={handleInputChange}
				label={`Number of Seats ($${company.price_per_user?.toFixed(2)} per seat per year):`}
				errorMessage={validationError}
			/>

			{!calculatedAmounts && <MinimumSeatsMessage minUsers={company.min_users_to_add || 0} />}
			{calculationError && <CalculationErrorMessage calculationError={calculationError} />}

			{calculatedAmounts && <PricingInfo calculatedAmounts={calculatedAmounts} company={company} />}
		</ContentModal>
	);
};

export default AddUsersModal;
